/**
 * TestRunPanel.tsx — runs the config-backed calc via the config-eval test-run
 * endpoint. Renders: outputs, recommendations with the per-candidate score
 * trace (S3 breakdown shape), and read-only DB prices.
 */

import { useState } from 'react';
import { Play, Loader2 } from 'lucide-react';
import { calculatorBuilderApi, type StudioInputField, type ConfigTestRunResponse } from '../../../api/calculatorBuilder';
import { StudioPanel, field, btnAccent, label } from './ui';

interface Props {
  category: string;
  inputs: StudioInputField[];
}

function formatINR(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  const s = Math.round(Math.abs(n)).toString();
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  const grouped = rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3 : last3;
  return '₹' + grouped;
}

export default function TestRunPanel({ category, inputs }: Props) {
  const [vals, setVals] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ConfigTestRunResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setRunning(true);
    setErr(null);
    try {
      // Coerce numbers/booleans by declared field type.
      const payload: Record<string, unknown> = {};
      for (const f of inputs) {
        const raw = vals[f.key];
        if (raw == null || raw === '') continue;
        if (f.type === 'number') payload[f.key] = Number(raw);
        else if (f.type === 'toggle') payload[f.key] = raw === 'true';
        else payload[f.key] = raw;
      }
      const res = await calculatorBuilderApi.testRunConfig(category, payload);
      setResult(res);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Test run failed');
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  return (
    <StudioPanel
      title="Test run"
      subtitle="Runs the live config. Shows outputs, ranked recommendations with the score trace, and read-only prices."
      right={
        <button type="button" style={btnAccent} onClick={run} disabled={running}>
          {running ? <Loader2 size={14} className="spin" /> : <Play size={14} />} Run
        </button>
      }
    >
      {/* inputs form */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
        {inputs.map((f) => (
          <div key={f.key}>
            <span style={label}>{f.label}{f.unit ? ` (${f.unit})` : ''}</span>
            {f.type === 'select' ? (
              <select style={{ ...field, width: '100%' }} value={vals[f.key] ?? ''} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}>
                <option value="">—</option>
                {(f.options ?? []).map((o) => (
                  <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                ))}
              </select>
            ) : f.type === 'toggle' ? (
              <select style={{ ...field, width: '100%' }} value={vals[f.key] ?? 'false'} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            ) : (
              <input
                style={{ ...field, width: '100%' }}
                type={f.type === 'number' ? 'number' : 'text'}
                value={vals[f.key] ?? ''}
                onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
              />
            )}
          </div>
        ))}
      </div>

      {err && <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--danger, #dc2626)' }}>{err}</div>}

      {result && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* outputs */}
          <div>
            <span style={label}>Outputs</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {Object.entries(result.outputs ?? {}).map(([k, v]) => (
                <div key={k} style={{ border: '1px solid var(--line, #e5e7eb)', borderRadius: 8, padding: '6px 10px', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--ink-3, #6b7280)', fontFamily: 'ui-monospace, monospace' }}>{k}</span>
                  <span style={{ marginLeft: 8, fontWeight: 700, color: 'var(--ink-1, #1f2937)' }}>{String(v)}</span>
                </div>
              ))}
              {Object.keys(result.outputs ?? {}).length === 0 && (
                <span style={{ fontSize: 12.5, color: 'var(--ink-3, #6b7280)' }}>No outputs.</span>
              )}
            </div>
          </div>

          {/* recommendations with score trace */}
          {(result.recommendations?.length ?? 0) > 0 && (
            <div>
              <span style={label}>Recommendations (with score trace)</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {result.recommendations!.map((rc, i) => (
                  <div key={i} style={{ border: '1px solid var(--line, #e5e7eb)', borderRadius: 9, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      {rc.slot && (
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--accent, #4f46e5)' }}>{rc.slot}</span>
                      )}
                      <span style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{rc.sku}</span>
                      {rc.score != null && (
                        <span style={{ fontSize: 12, color: 'var(--ink-3, #6b7280)' }}>score {rc.score.toFixed(3)}</span>
                      )}
                      <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{formatINR(rc.priceInr)}</span>
                    </div>
                    {rc.breakdown && rc.breakdown.length > 0 && (
                      <table style={{ marginTop: 8, borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                        <thead>
                          <tr style={{ color: 'var(--ink-3, #6b7280)', textAlign: 'left' }}>
                            <th style={{ padding: '2px 6px' }}>criterion</th>
                            <th style={{ padding: '2px 6px' }}>raw</th>
                            <th style={{ padding: '2px 6px' }}>norm</th>
                            <th style={{ padding: '2px 6px' }}>weight</th>
                            <th style={{ padding: '2px 6px' }}>contribution</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rc.breakdown.map((b, bi) => (
                            <tr key={bi} style={{ borderTop: '1px solid var(--line, #e5e7eb)' }}>
                              <td style={{ padding: '2px 6px', fontFamily: 'ui-monospace, monospace' }}>{b.criterionKey}</td>
                              <td style={{ padding: '2px 6px' }}>{b.raw.toFixed(3)}</td>
                              <td style={{ padding: '2px 6px' }}>{b.normalized.toFixed(3)}</td>
                              <td style={{ padding: '2px 6px' }}>{b.weight.toFixed(2)}</td>
                              <td style={{ padding: '2px 6px', fontWeight: 700 }}>{b.contribution.toFixed(3)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-3, #6b7280)' }}>
                Prices are read-only from Product.unitPrice.
              </div>
            </div>
          )}
        </div>
      )}
    </StudioPanel>
  );
}
